import { useState, useEffect } from 'react';
import {
  Typography, Button, Card, Space, Tooltip, Modal, Form, Input, Row, Col, message,
} from 'antd';
import { CopyOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useCreateCredentials } from 'hooks/api';
import { getCredentials } from 'services/credentials.service';

const { Title, Text } = Typography;

interface CredentialEntry {
  _id: string;
  title: string;
  values: Record<string, string>;
}

const CredentialsList = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [credentials, setCredentials] = useState<CredentialEntry[]>([]);
  const [form] = Form.useForm();
  const { mutateAsync: create, isLoading: creating } = useCreateCredentials();

  useEffect(() => {
    setIsLoading(true);
    getCredentials()
      .then((r) => {
        setCredentials(r.data || []);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value);
    message.success('Copied!');
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const valuesMap: Record<string, string> = {};
    (values.entries || []).forEach(({ key, value }: { key: string; value: string }) => {
      if (key) valuesMap[key] = value;
    });
    await create({ title: values.title, values: valuesMap });
    form.resetFields();
    setModalOpen(false);
    setIsLoading(true);
    getCredentials()
      .then((r) => setCredentials(r.data || []))
      .finally(() => setIsLoading(false));
  };

  return (
    <div>
      <Space style={{ marginBottom: 24, justifyContent: 'space-between', width: '100%' }}>
        <Title level={2} style={{ margin: 0 }}>Credentials</Title>
        <Button type='primary' icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
          Add Credentials
        </Button>
      </Space>

      {isLoading && <Text type='secondary'>Loading...</Text>}

      <Row gutter={[16, 16]}>
        {credentials.map((cred) => (
          <Col key={cred._id} xs={24} sm={12} md={8} lg={6}>
            <Card title={cred.title} size='small'>
              <Space direction='vertical' style={{ width: '100%' }} size={8}>
                {Object.entries(cred.values).map(([key, val]) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <Text type='secondary' style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {key}
                      :
                    </Text>
                    <Text style={{ wordBreak: 'break-all', flex: 1 }}>{val}</Text>
                    <Tooltip title='Copy'>
                      <Button
                        size='small'
                        type='text'
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(val)}
                        style={{ flexShrink: 0 }}
                      />
                    </Tooltip>
                  </div>
                ))}
              </Space>
            </Card>
          </Col>
        ))}
      </Row>

      <Modal
        title='Add Credentials'
        visible={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={creating}
        okText='Create'
        destroyOnClose
      >
        <Form form={form} layout='vertical'>
          <Form.Item name='title' label='Title' rules={[{ required: true, message: 'Title is required' }]}>
            <Input placeholder='e.g. My Service' />
          </Form.Item>

          <Form.List name='entries' initialValue={[{ key: '', value: '' }]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} align='baseline' style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item
                      {...restField}
                      name={[name, 'key']}
                      rules={[{ required: true, message: 'Key required' }]}
                    >
                      <Input placeholder='key' />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'value']}
                      rules={[{ required: true, message: 'Value required' }]}
                    >
                      <Input placeholder='value' />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
                <Button type='dashed' onClick={() => add()} block icon={<PlusOutlined />}>
                  Add field
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default CredentialsList;
